import {MutationTree} from "vuex";
import moment from 'moment';
import IProject from "@/modules/ProjectManager/ts/IProject";
import ICategory from "@/modules/ProjectManager/ts/ICategory";
import IProjectStats from "@/modules/ProjectManager/ts/IProjectStats";
import IFilter from "@/modules/PanelistManager/ts/IFilter";
import IPanelist from "@/modules/PanelistManager/ts/PanelistInterface";
import IParticipant from "@/modules/ProjectManager/ts/IParticipant";

export const mutations: MutationTree<any> = {

    setActiveTab(state: any, payload: boolean) {
        state.activeTabModel = payload;
    },

    setProjectsLoading(state: any, payload: any) {
        state.projectsLoading = payload;
    },

    setProjects(state: any, payload: any) {
        state.projectsCacheExpires = moment().add(5, 'minutes');
        state.projects = payload;
    },

    setRecontactParticipants(state: any, payload: any) {
        state.recontactParticipants = payload;
    },

    appendRecontactParticipants(state: any, payload: any) {

        payload.forEach((panelist: IPanelist) => {

            const index = state.recontactParticipants.findIndex((participant: IParticipant) => participant.panelist_id === panelist.panelist_id);

            if (index !== -1) {
                state.recontactParticipants.splice(index, 1, panelist);
            } else {
                state.recontactParticipants.push(panelist);
            }
        })
    },

    appendSubPanelIds(state: any, payload: Array<number>) {
        payload.forEach((subPanelId: number) => {
            const found = state.selectedSubPanelIds.find((id: number) => id === subPanelId);

            if (!found) {
                state.selectedSubPanelIds.push(subPanelId);
            }
        });
    },

    setSelectedSubPanelIds(state: any, payload: any) {
        state.selectedSubPanelIds = payload;
    },

    setActiveTableOptions(state: any, payload: object) {
        state.activeTableOptions = payload;
    },

    setArchivedProjects(state: any, payload: any) {
        state.archivedProjects = payload;
    },

    setArchivedTableOptions(state: any, payload: object) {
        state.archivedTableOptions = payload;
    },

    setArchivedTableSearch(state: any, payload: string) {
        state.archivedTableSearch = payload;
    },

    createProject(state: any, project: IProject) {
        state.projects.push(project);
        state.prominentProject = JSON.parse(JSON.stringify(project));
    },

    appendFilter(state: any, payload: any) {
        const found = state.activeTableFilters.find((f: IFilter) => f.type === payload.type);
        if (!found) state.activeTableFilters.push(payload);
    },

    setProminentProject(state: any, payload: IProject) {
        if (payload) {
            state.prominentProject = JSON.parse(JSON.stringify(payload));
        }
    },

    clearProminentProject(state: any) {
        state.prominentProject = null;
    },

    restoreFilters(state: any, payload: Array<any>) {

        state.activeTableFilters = state.activeTableFilters.map((f: any) => {

            const found = payload.find((filter: any) => filter.type === f.type);

            if (found) {
                return Object.assign({}, f, found);
            } else {
                f.model = false;
                return f;
            }
        })
    },

    updateProject(state: any, project: IProject) {

        let index = null;

        let stateItem = project.status === 'archived' ? 'archivedProjects' : 'projects';

        index = state[stateItem].findIndex((p: IProject) => {
            return p.id === project.id;
        });

        if (index !== -1) {
            state[stateItem].splice(index, 1, project);
            state.prominentProject = project;
        }
    },

    updateProjectStatus(state: any, data: any) {

        let found = state.projects.find((p: IProject) => {
            return p.id === data.id;
        });

        // Check if in archived projects
        if (!found) {
            const index = state.archivedProjects.findIndex((p: IProject) => {
                return p.id === data.id;
            });

            found = state.archivedProjects[index];

            // Move to projects
            state.projects.push(found);
            state.archivedProjects.splice(index, 1);
        }

        if (state.prominentProject && state.prominentProject.id === data.id) {
            state.prominentProject.status = data.status;
        }

        found.status = data.status;
    },

    deleteProject(state: any, id: number) {
        let indexProject = state.projects.findIndex((project: IProject) => {
            return project.id == id;
        });

        state.projects.splice(indexProject, 1);

        let indexArchived = state.archivedProjects.findIndex((project: IProject) => {
            return project.id == id;
        });

        state.archivedProjects.splice(indexArchived, 1);
    },

    setCategoriesLoading(state: any, payload: any) {
        state.categoriesLoading = payload;
    },

    setCategories(state: any, payload: any) {
        state.categoriesCacheExpires = moment().add(15, 'minutes');
        state.categories = payload;
    },

    setActiveTableSortBy(state: any, payload: any) {
        state.activeTableSortBy = payload;
    },

    setActiveTableSortDesc(state: any, payload: any) {
        state.activeTableSortDesc = payload;
    },

    setActiveTableSearch(state: any, payload: any) {
        state.activeTableSearch = payload;
    },

    createCategory(state: any, payload: ICategory) {
        state.categories.push(payload);
    },

    updateCategory(state: any, payload: ICategory) {
        let found = state.categories.find((category: ICategory) => category.id === payload.id);

        found = Object.assign(found, payload);
    },

    deleteCategory(state: any, category: ICategory) {
        let index = state.categories.findIndex((cat: ICategory) => cat.id === category.id);

        state.categories.splice(index, 1);
    },

    saveCategoryId(state: any, id: number) {
        let found = state.categories.find((category: ICategory) => category.id === -1);

        if (!found) console.error("Category not found with ID 999999");

        found.id = id;
    },

    setProjectStatsLoading(state: any, payload: any) {
        state.projectStatsLoading = payload;
    },

    setProjectStats(state: any, payload: any) {

        let index = state.projectStats.findIndex((stats: IProjectStats) => {
            return stats.id === payload.id
        });

        // Replace
        if (index !== -1) {
            state.projectStats.splice(index, 1, payload);
        } else {
            state.projectStats.push(payload);
        }
    },

    setProjectGeoStatsLoading(state: any, payload: any) {
        state.projectGeoStatsLoading = payload;
    },

    setProjectGeoStats(state: any, payload: any) {

        let index = state.projectGeoStats.findIndex((stats: IProjectStats) => {
            return stats.id === payload.id
        });

        // Replace
        if (index !== -1) {
            state.projectGeoStats.splice(index, 1, payload);
        } else {
            state.projectGeoStats.push(payload);
        }
    },

    setProjectErrors(state: any, payload: any) {
        state.projectErrors = payload;
    },

    deleteProjectErrors(state: any, projectId: number) {
        let project = state.projects.find((project: IProject) => project.id === projectId);
        project.error_count = 0;
    },

    setGallery(state: any, payload: any) {
        state.galleryCacheExpires = moment().add(60, 'minutes');
        state.gallery = payload;
    },

    insertToGallery(state: any, payload: any) {
        state.gallery.push(payload);
    },

    deleteGalleryItem(state: any, payload: string) {
        const index = state.gallery.findIndex((item: string) => item === payload);
        state.gallery.splice(index, 1);
    }
};
